/**
 * Mandate Layout — shell for Mandate Module.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MandateSidebar } from './MandateSidebar';
import { MandateNoTransition } from './MandateNoTransition';
import { ToastProvider } from '../ui/Toast';
import { useMandateSpaceBootstrap } from '../../hooks/useMandateSpaceBootstrap';
import { useWorkspaceContextBootstrap } from '../../hooks/useWorkspaceContextBootstrap';
import { useWorkspaceScopedStores } from '../../hooks/useWorkspaceScopedStores';
import { ProductSurfaceProvider } from '../../contexts/ProductSurfaceContext';
import {
  MANDATE_BASE,
  rewritePathForMandateShell,
  setMandateShellActive,
} from '../../lib/productSurfacePaths';
import { useMobileExperience } from '../../mobile/featureFlag';
import { MandateMobileShell } from '../../mobile/MandateMobileShell';

interface MandateLayoutProps {
  children: ReactNode;
}

// Platform sub-paths that embed full data views — require space to be ready
// before rendering to prevent non-mandate data from flashing through.
const PLATFORM_PATHS = ['/items', '/sessions', '/reports', '/platform', '/lineage', '/tools'];

export default function MandateLayout({ children }: MandateLayoutProps) {
  const isMobile = useMobileExperience();
  // The same workspace bootstrap the Priorities and Plans shells run: without it a
  // direct load of /apps/mandate/* never resolves the tenant, so the space switcher
  // reads "Loading spaces…" for good and the embedded pages have no workspace.
  useWorkspaceContextBootstrap();
  // The embedded platform pages read the workspace-scoped stores (items, sessions,
  // tracks…) the Priorities shell loads; load them here too, and again when the
  // mandate bootstrap switches to the transition's workspace.
  useWorkspaceScopedStores();
  const { isReady, status } = useMandateSpaceBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMandateShellActive(true);
    return () => setMandateShellActive(false);
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element).closest('a[href]');
      if (!anchor || anchor.getAttribute('target') === '_blank') return;

      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/prioritization/')) return;

      const rewritten = rewritePathForMandateShell(href);
      if (rewritten === href) return;

      event.preventDefault();
      navigate(rewritten);
    };

    main.addEventListener('click', onClick);
    return () => main.removeEventListener('click', onClick);
  }, [navigate]);

  // Platform data views wait until the mandate space is confirmed active; with no
  // transition there is no such space, so they say so rather than wait for one.
  // Mandate-native pages (dashboard, setup, priorities, etc.) render immediately.
  const isPlatformPath = PLATFORM_PATHS.some((p) =>
    location.pathname.includes(p)
  );
  const platformWaiting = isPlatformPath && status === 'loading';
  const content = !isPlatformPath || isReady
    ? children
    : status === 'no-transition'
      ? <MandateNoTransition />
      : status === 'failed'
        ? (
          <p className="text-sm text-[var(--text-ter)] py-8 text-center">
            Your Mandate space could not be opened. Reload to try again.
          </p>
        )
        : null;

  if (isMobile) {
    return (
      <ProductSurfaceProvider basePath={MANDATE_BASE}>
        <MandateMobileShell platformReady={!platformWaiting}>{content}</MandateMobileShell>
      </ProductSurfaceProvider>
    );
  }

  return (
    <ProductSurfaceProvider basePath={MANDATE_BASE}>
      <ToastProvider>
        <div className="min-h-screen bg-[var(--bg-raised)]">
          <MandateSidebar />
          <div className="pl-64 flex flex-col min-h-screen">
            <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] h-14 px-6 flex-shrink-0 flex items-center justify-between" />
            <main ref={mainRef} className="flex-1 pt-[22px] pb-6 px-6">
              {platformWaiting ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-5 h-5 animate-spin text-[var(--text-ter)]" />
                </div>
              ) : (
                content
              )}
            </main>
          </div>
        </div>
      </ToastProvider>
    </ProductSurfaceProvider>
  );
}
